import moment from 'moment';
import 'moment-timezone';

const DateService = {
  formats: {
    DD_MM_YYYY_HH_MM_SS_SSS: 'DD/MM/YYYY HH:mm:ss:SSS',
    DD_MM_YYYY: 'DD/MM/YYYY hh:mm A',
    DD_MM__YYYY: 'DD/MM/YYYY',
    DD_MM_YYYY__A_Z: 'DD/MM/YYYY hh:mm',
    MMM_DD_YYYY: 'MMM DD, YYYY',
    HH_MM_SS: 'HH:mm:ss',
    dd_mmm__yyyy: 'DD MMM, YYYY',
  },

  // Get user timezone with fallback
  getUserTimezone() {
    try {
      const userInfo = localStorage.getItem('userInfo');
      if (!userInfo) {
        console.warn('UserInfo not found in localStorage');
        return null;
      }

      const parsed = JSON.parse(userInfo);
      const timeZone = parsed.timeZone;

      if (!timeZone) {
        console.warn('Timezone not found in userInfo');
        return null;
      }

      // Validate timezone
      if (!moment.tz.zone(timeZone)) {
        console.error('Invalid timezone format:', timeZone);
        return null;
      }

      return timeZone;
    } catch (error) {
      console.error('Error getting user timezone:', error);
      return null;
    }
  },

  // Convert and format the date to the user's timezone
  formatDate(date: string | null, format: string): string {
    if (!date) {
      return 'Invalid Date';
    }

    try {
      const timeZone = this.getUserTimezone();

      if (!timeZone) {
        // Fallback to browser timezone or UTC
        const fallbackTz =
          Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
        console.warn(`Using fallback timezone: ${fallbackTz}`);

        const parsedDate = moment.utc(date, 'DD/MM/YYYY HH:mm');
        if (!parsedDate.isValid()) {
          return 'Invalid Date';
        }
        return parsedDate.tz(fallbackTz).format(format);
      }

      console.log('Using timezone:', timeZone);

      // Parse the date - try multiple formats
      let parsedDate = moment.utc(date, 'DD/MM/YYYY HH:mm');

      // If first format fails, try without time
      if (!parsedDate.isValid()) {
        parsedDate = moment.utc(date, 'DD/MM/YYYY');
      }

      if (!parsedDate.isValid()) {
        console.error('Could not parse date:', date);
        return 'Invalid Date';
      }

      // Convert UTC date to the user's timeZone
      return parsedDate.tz(timeZone).format(format);
    } catch (error) {
      console.error('Error formatting date:', error);
      console.error('Date input:', date);
      console.error('Format:', format);
      return 'Error Formatting Date';
    }
  },

  // Helper method to debug timezone issues
  debugTimezone() {
    console.log('=== Timezone Debug Info ===');
    console.log(
      'Browser timezone:',
      Intl.DateTimeFormat().resolvedOptions().timeZone
    );
    console.log(
      'Available moment timezones sample:',
      moment.tz.names().slice(0, 10)
    );

    const userTz = this.getUserTimezone();
    console.log('User timezone:', userTz);

    if (userTz) {
      console.log('Timezone is valid:', !!moment.tz.zone(userTz));
    }
  },
};

export default DateService;
